import React, { useEffect, useRef, useState } from 'react';

import Typography from '@material-ui/core/Typography';
import { fade, Theme } from '@material-ui/core/styles';
import styled from '@material-ui/core/styles/styled';
import { isWidthUp } from '@material-ui/core/withWidth';
import Swiper from 'react-id-swiper';
import { Swiper as SwiperClass } from 'swiper/js/swiper.esm';

import ChevronLeftIcon from '../../assets/icons/ChevronLeftIcon';
import ChevronRightIcon from '../../assets/icons/ChevronRightIcon';
import { BIG_MARGIN, colorsPalette, LARGE_MARGIN, MIDDLE_MARGIN, useWidth } from '../../theme';
import { Background } from '../../types';
import useIsSsr from '../../utils/hooks/useIsSsr';
import Slide from './components/Slide/Slide';
import StyledSelect from './components/StyledSelect';

const swiperSetting = {
  slidesPerView: 1,
  spaceBetween: 20,
  pagination: {
    el: '.swiper-pagination',
    clickable: true,
  },
};

const TabsSliderBlock = ({ data }) => {
  const width = useWidth();
  const isMobile = !isWidthUp('md', width);
  const isSsr = useIsSsr();

  const swiperRef = useRef<SwiperClass>(null);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  useEffect(() => {
    const handler = () => setSelectedTabIndex(swiperRef.current?.activeIndex);
    swiperRef.current?.on('realIndexChange', handler);

    return () => swiperRef.current?.off('realIndexChange', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swiperRef.current]);

  const goNext = () => swiperRef.current?.slideNext();
  const goPrev = () => swiperRef.current?.slidePrev();
  const slideTo = (index) => swiperRef.current?.slideTo(index);

  const currentIndex = swiperRef.current?.activeIndex;

  const slides = data?.content;
  const options = slides.map((slide, index) => ({ value: index, display: slide.title }));

  return (
    <Wrapper isDark={data?.background === Background.Dark}>
      <Container>
        <Title data-testid="tabsSliderBlockTitle" variant="h2">
          {data?.heading}
        </Title>
        {isMobile ? (
          <StyledSelect
            items={options}
            value={options[selectedTabIndex]}
            onChange={({ value }) => slideTo(value)}
          />
        ) : (
          <Tabs>
            {slides.map((slide, index) => (
              <Tab
                data-testid="tabsSliderBlockTab"
                key={index}
                selected={selectedTabIndex === index}
                onClick={() => slideTo(index)}
              >
                {slide.title}
              </Tab>
            ))}
          </Tabs>
        )}
        <SwiperWrapper>
          <NavLeft disabled={currentIndex === 0} onClick={goPrev}>
            <StyledLeftChevron />
          </NavLeft>
          {!isSsr && (
            <Swiper getSwiper={(swiper) => (swiperRef.current = swiper)} {...swiperSetting}>
              {slides.map(({ id, image, theses, title }) => (
                <Slide key={id} imagePath={image} listItems={theses} title={title} />
              ))}
            </Swiper>
          )}
          <NavRight disabled={currentIndex === slides.length - 1} onClick={goNext}>
            <StyledRightChevron />
          </NavRight>
        </SwiperWrapper>
      </Container>
    </Wrapper>
  );
};

export default TabsSliderBlock;

const Container = styled('div')(({ theme }: { theme: Theme }) => ({
  maxWidth: 1440,
  margin: '0 auto',
  padding: '0 16px',
  boxSizing: 'border-box',
  [theme.breakpoints.up('md')]: {
    padding: '0 24px',
  },
}));
const NavLeft = styled('div')(({ theme, disabled }: { theme: Theme; disabled: boolean }) => ({
  display: 'none',
  cursor: !disabled ? 'pointer' : 'initial',
  [theme.breakpoints.up('lg')]: {
    position: 'absolute',
    top: 0,
    left: 0,
    display: 'flex',
    alignItems: 'center',
    height: '100%',
  },
  '&:hover svg': {
    opacity: !disabled ? 1 : 0.5,
  },
}));
const NavRight = styled('div')(({ theme, disabled }: { theme: Theme; disabled: boolean }) => ({
  display: 'none',
  cursor: !disabled ? 'pointer' : 'initial',
  [theme.breakpoints.up('lg')]: {
    position: 'absolute',
    top: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    height: '100%',
  },
  '&:hover svg': {
    opacity: !disabled ? 1 : 0.5,
  },
}));
const Wrapper = styled('div')(({ theme, isDark }: { theme: Theme; isDark: boolean }) => ({
  padding: `${BIG_MARGIN}px 0`,
  background: isDark ? colorsPalette.blueDark : colorsPalette.blackSecondary,
  [theme.breakpoints.up('md')]: {
    padding: `${LARGE_MARGIN}px 0`,
  },
}));
const Title = styled(Typography)(({ theme }: { theme: Theme }) => ({
  marginBottom: 24,
  textAlign: 'center',
  fontSize: 24,
  [theme.breakpoints.up('md')]: {
    marginBottom: MIDDLE_MARGIN,
    fontSize: 28,
  },
}));
const SwiperWrapper = styled('div')(({ theme }: { theme: Theme }) => ({
  position: 'relative',
  [theme.breakpoints.up('lg')]: {
    padding: '0 100px',
  },
  '& .swiper-pagination': {
    [theme.breakpoints.up('lg')]: {
      display: 'none',
    },
  },
}));
const StyledRightChevron = styled(ChevronRightIcon)({
  width: 44,
  marginLeft: BIG_MARGIN,
  opacity: 0.5,
  transition: '0.3s opacity',
});
const StyledLeftChevron = styled(ChevronLeftIcon)({
  width: 44,
  marginRight: BIG_MARGIN,
  opacity: 0.5,
  transition: '0.3s opacity',
});
const Tabs = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  marginBottom: 26,
});
const Tab = styled('span')(({ selected }: { selected: boolean }) => ({
  position: 'relative',
  margin: '0 14px 10px',
  color: selected ? colorsPalette.white : fade(colorsPalette.white, 0.5),
  cursor: 'pointer',
  fontSize: 18,
  '&:after': {
    content: '""',
    position: 'absolute',
    top: '100%',
    left: 0,
    height: 2,
    width: selected ? '100%' : '0%',
    background: colorsPalette.blue,
    transition: '0.3s width',
  },
}));
